import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from 'vue';
import { useColor } from '@/composables/color';
import { useToaster } from '@/composables/toaster';
import QrColorPicker from './QrColorPicker.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import type { CanvasEx } from '@/typings/canvas';
import type {
    FontColorOption,
    QrStyleOptions,
    VectorPlaceholderElement
} from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import {
    DEFAULT_QR_STYLE_OPTIONS,
    TRANSPARENT_COLOR,
    isStyledQrConfig,
    updateStyledQrOptions
} from '@/utils/qrCodeUtils';

type ColorKey = 'dotsColor' | 'cornersSquareColor' | 'cornersDotColor' | 'backgroundColor';


export default /*#__PURE__*/_defineComponent({
  __name: 'ColorProperties',
  props: {
    canvas: null,
    vectorElement: null,
    editMode: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    canvas: CanvasEx;
    vectorElement?: VectorPlaceholderElement;
    editMode?: boolean;
};

const COLOR_ITEMS: { label: string; key: ColorKey }[] = [
    { label: 'Dots Color', key: 'dotsColor' },
    { label: 'Corner Square Color', key: 'cornersSquareColor' },
    { label: 'Corner Dot Color', key: 'cornersDotColor' },
    { label: 'Background Color', key: 'backgroundColor' }
];



const model = reactive({
    dotsColor: undefined as ColorObject | undefined,
    cornersSquareColor: undefined as ColorObject | undefined,
    cornersDotColor: undefined as ColorObject | undefined,
    backgroundColor: undefined as ColorObject | undefined,
    transparentBackground: true
});

const { getColorFromString, hexToHexa } = useColor();

const parseColor = (colorValue: string | undefined, defaultHex: string): ColorObject | undefined => {
    if (colorValue === TRANSPARENT_COLOR) return undefined;

    const color = getColorFromString(colorValue || defaultHex);
    return { hex: color.hex, alpha: color.a, cmyk: '' };
};

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || !isStyledQrConfig(element.qrConfig)) return;

        const options = element.qrConfig.options;
        model.dotsColor = parseColor(options.dotsOptions.color, DEFAULT_QR_STYLE_OPTIONS.dotsOptions.color);
        model.cornersSquareColor = parseColor(
            options.cornersSquareOptions.color,
            DEFAULT_QR_STYLE_OPTIONS.cornersSquareOptions.color
        );
        model.cornersDotColor = parseColor(
            options.cornersDotOptions.color,
            DEFAULT_QR_STYLE_OPTIONS.cornersDotOptions.color
        );
        model.transparentBackground = options.backgroundOptions.color === TRANSPARENT_COLOR;
        model.backgroundColor = parseColor(options.backgroundOptions.color, '#FFFFFF');
    },
    { immediate: true }
);

const disabled = computed(() => !!props.vectorElement?.locked);

const currentOptions = computed<QrStyleOptions | undefined>(() =>
    isStyledQrConfig(props.vectorElement?.qrConfig) ? props.vectorElement!.qrConfig.options : undefined
);

const toaster = useToaster();
const applyOptions = async (options: Partial<QrStyleOptions>) => {
    try {
        await updateStyledQrOptions(props.vectorElement, props.canvas, options, props.editMode);
    } catch (error) {
        console.error('Error updating QR code colors:', error);
        toaster.error('An error occurred while generating the QR code');
    }
};

const onColorSelected = (key: ColorKey, color: FontColorOption) => {
    const options = currentOptions.value;
    if (!options) return;

    const colorObject = color as ColorObject;
    model[key] = { ...colorObject };
    const value = hexToHexa(colorObject.hex, colorObject.alpha);

    switch (key) {
        case 'dotsColor':
            return applyOptions({ dotsOptions: { ...options.dotsOptions, color: value } });
        case 'cornersSquareColor':
            return applyOptions({ cornersSquareOptions: { ...options.cornersSquareOptions, color: value } });
        case 'cornersDotColor':
            return applyOptions({ cornersDotOptions: { ...options.cornersDotOptions, color: value } });
        case 'backgroundColor':
            model.transparentBackground = false;
            return applyOptions({ backgroundOptions: { ...options.backgroundOptions, color: value } });
    }
};

const onTransparentBackgroundToggled = (value: boolean) => {
    const options = currentOptions.value;
    if (!options) return;

    model.transparentBackground = value;
    const color = value
        ? TRANSPARENT_COLOR
        : hexToHexa(model.backgroundColor?.hex || '#FFFFFF', model.backgroundColor?.alpha ?? 1);

    applyOptions({ backgroundOptions: { ...options.backgroundOptions, color } });
};

const visibleColorItems = computed(() =>
    COLOR_ITEMS.filter((item) => item.key !== 'backgroundColor' || !model.transparentBackground)
);

return { __sfc: true,COLOR_ITEMS, props, model, getColorFromString, hexToHexa, parseColor, disabled, currentOptions, toaster, applyOptions, onColorSelected, onTransparentBackgroundToggled, visibleColorItems, QrColorPicker, CCheckbox }
}

})